import React from 'react';

import APIError from 'types/api_error';
import {
  Deployment,
  DeploymentState,
  DeploymentConfiguration,
} from 'types/deployment';
import Pipeline from 'types/pipeline';

import { sanitizeObject } from 'services/object';
import { useAllDeployments } from 'deployments/hooks/useAllDeployments';
import { useGateways } from 'gateways/hooks/useGateways';

import { ActivePipelineContext } from 'pipelines/context/active_pipeline';

import { FormMessage } from 'components/FormMessage/FormMessage';
import { Text } from 'components/Text/Text';

import { DeploymentBulkOverrideFormStep2 } from './Step2';
import { DeploymentBulkOverrideFormStepper } from './Stepper';
import { DeploymentFormProvider } from '../context/deployment_form_provider';
import { DeploymentsFormSelect } from '../DeploymentsSelect/DeploymentsSelect';
import { mergeConfigurations } from './merge_configurations';

type BulkOverrideDeploymentsFormContentProps = {
  shouldStartImmediately: boolean;
  pipeline: Pipeline;
  deploymentIds?: Record<Deployment['id'], boolean>;
  mutate: (
    params: Array<{
      id: string;
      state: DeploymentState;
      configuration: DeploymentConfiguration;
    }>
  ) => void;
  error: APIError | null;
  onFormStepChange: (page: number) => void;
  onSelect: (deploymentIds: Record<Deployment['id'], boolean>) => void;
};

function BulkOverrideDeploymentsFormContent(
  {
    shouldStartImmediately,
    pipeline,
    deploymentIds: initialDeploymentIds = {},
    mutate,
    error,
    onFormStepChange,
    onSelect,
  }: BulkOverrideDeploymentsFormContentProps,
  formRef: React.ForwardedRef<HTMLFormElement>
) {
  const [formStep, setFormStep] = React.useState(1);

  const [selection, setSelection] = React.useState(initialDeploymentIds);
  const selectedDeploymentsCount = Object.keys(selection).length;

  const {
    data: deployments,
    isLoading,
    isError,
  } = useAllDeployments({
    params: {
      deployment_ids:
        selectedDeploymentsCount > 0 ? Object.keys(selection) : undefined,
      pipeline_ids: [pipeline.id],
    },
    enabled: formStep > 1,
  });

  const gatewayIds = [
    ...new Set(deployments?.map(({ gateway_id }) => gateway_id)),
  ];

  const { data: gateways, isLoading: isLoadingGateways } = useGateways({
    params: {
      gateway_ids: gatewayIds,
      limit: gatewayIds.length,
    },
    enabled: deployments && gatewayIds.length > 0,
  });

  async function redeployAll(
    overrideConfiguration: DeploymentConfiguration = {}
  ) {
    if (!deployments || !gateways) {
      return;
    }

    const deploymentParams = deployments.map(
      ({ id, gateway_id, configuration }) => {
        const gateway = gateways.data.find(({ id }) => id === gateway_id);

        let state: DeploymentState = shouldStartImmediately
          ? 'running'
          : 'stopped';

        if (gateway?.status === 'offline') {
          state = 'stopped';
        }

        return {
          id,
          state,
          configuration: sanitizeObject(
            mergeConfigurations(configuration, overrideConfiguration)
          ),
        };
      }
    );

    mutate(deploymentParams);
  }

  const handleStep1Continue = React.useCallback(
    (selectedDeployments: Record<Deployment['id'], boolean>) => {
      setSelection(selectedDeployments);
      setFormStep(2);
      onFormStepChange(2);
      onSelect(selectedDeployments);
    },
    [onFormStepChange, onSelect]
  );

  return (
    <ActivePipelineContext.Provider value={pipeline}>
      <DeploymentBulkOverrideFormStepper
        currentStep={formStep}
        count={Object.keys(selection).length}
        onNavigate={(step) => {
          setFormStep(step);
          onFormStepChange(step);
        }}
      />

      {error && (
        <FormMessage icon="error" intent="danger">
          <Text type="paragraph">
            <strong>{error.message}</strong>
          </Text>
          {error.code === 'invalid-pipeline-definition' && (
            <Text type="paragraph">
              There may be new required properties in the workflow that cannot
              be bypassed in the bulk override. Please update the deployments
              individually.
            </Text>
          )}
        </FormMessage>
      )}

      {formStep === 1 && (
        <DeploymentsFormSelect
          defaultValue={selection}
          pipeline={pipeline}
          onConfirmSelection={handleStep1Continue}
        />
      )}

      {formStep === 2 && (
        <DeploymentFormProvider pipeline={pipeline}>
          <DeploymentBulkOverrideFormStep2
            onSubmit={redeployAll}
            isLoading={isLoading || isLoadingGateways}
            isError={isError}
            ref={formRef}
          />
        </DeploymentFormProvider>
      )}
    </ActivePipelineContext.Provider>
  );
}

const forwardedRef = React.forwardRef(BulkOverrideDeploymentsFormContent);

export { forwardedRef as BulkOverrideDeploymentsFormContent };
